import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { useState, useEffect, useMemo } from "react";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { getBudgetFieldsConfig } from "@/other/constants/TaskBudget/taskBudgetViewColumnsData";
import { Label } from "@/modern/components/ui/label";
import { Combobox, TComboOption, TComboSelectedValue } from "@/modern/components/ui/combobox";
import { Card } from "@/modern/components/ui/card";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

interface BudgetField {
  id: string;
  budgetField: string;
  budgetFieldTitle: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  budgetValueFormatter?: any;
  isPercentage?: boolean;
}

const BudgetSettingsDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, userPreferences } = useSwivuBryntumContext();
  const [budgetFields, setBudgetFields] = useState<BudgetField[]>([]);
  const [budgetLabel, setBudgetLabel] = useState<string>("notDisplayed");
  const [actualDaysResourceTypes, setActualDaysResourceTypes] = useState<number[]>([]);

  // Budget label options
  const budgetLabelOptions: TComboOption[] = useMemo(() => {
    return [
      { value: "notDisplayed", label: "Not Displayed" },
      ...getBudgetFieldsConfig().map((field) => ({
        value: field.budgetField,
        label: field.budgetFieldTitle
      }))
    ];
  }, []);

  // Resource type options for Actual Days
  const resourceTypeOptions: TComboOption[] = useMemo(() => {
    if (!project) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resourceTypeStore = project.getCrudStore(STORE_KEYS.resourceTypeStore) as any;
    if (!resourceTypeStore) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resourceTypes = resourceTypeStore.query(() => true, true) || [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return resourceTypes.map((rt: any) => ({
      value: rt.id,
      label: rt.description || rt.name || ""
    }));
  }, [project]);

  useEffect(() => {
    loadSettings();
  }, [userPreferences]);

  const loadSettings = () => {
    if (!userPreferences) return;

    // Load budget fields order
    const budgetHoverOrderStr = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.budgetHoverOrder, "", true);
    let orderedFields: BudgetField[] = [];

    if (budgetHoverOrderStr) {
      try {
        const budgetHoverOrder = JSON.parse(budgetHoverOrderStr);
        if (Array.isArray(budgetHoverOrder) && budgetHoverOrder.length > 0) {
          const itemsToMove = budgetHoverOrder.reduce((prevState: BudgetField[], currentItem: string) => {
            const budgetItem = getBudgetFieldsConfig().find((item) => item.budgetField === currentItem);
            if (budgetItem) {
              prevState.push({
                ...budgetItem,
                id: budgetItem.budgetField
              });
            }
            return prevState;
          }, []);
          const remainingItems = getBudgetFieldsConfig().filter(
            (item) => !budgetHoverOrder.includes(item.budgetField)
          ).map(item => ({
            ...item,
            id: item.budgetField
          }));
          orderedFields = [...itemsToMove, ...remainingItems];
        }
      } catch {
        orderedFields = getBudgetFieldsConfig().map(item => ({
          ...item,
          id: item.budgetField
        }));
      }
    } else {
      orderedFields = getBudgetFieldsConfig().map(item => ({
        ...item,
        id: item.budgetField
      }));
    }

    setBudgetFields(orderedFields);

    // Load budget label
    const labelValue = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.budgetLabel, "notDisplayed", true);
    setBudgetLabel(labelValue || "notDisplayed");

    // Load actual days resource types
    const actualDaysStr = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.budgetActualDays, "", true);
    if (actualDaysStr) {
      try {
        const parsedValue = JSON.parse(actualDaysStr);
        setActualDaysResourceTypes(Array.isArray(parsedValue) ? parsedValue : []);
      } catch {
        setActualDaysResourceTypes([]);
      }
    } else {
      setActualDaysResourceTypes([]);
    }
  };

  const handleReorderFields = (movedItem: BudgetField, beforeItem: BudgetField | null) => {
    if (!userPreferences) return;

    const currentFields = [...budgetFields];
    const movedIndex = currentFields.findIndex((f) => f.id === movedItem.id);

    if (movedIndex === -1) return;

    // Remove from current position
    const [removed] = currentFields.splice(movedIndex, 1);

    // Insert at new position
    if (beforeItem === null) {
      // Moving to the end
      currentFields.push(removed);
    } else {
      // Find the target position after removal (indices have shifted)
      const beforeIndex = currentFields.findIndex((f) => f.id === beforeItem.id);
      if (beforeIndex !== -1) {
        // Insert before the target item
        currentFields.splice(beforeIndex, 0, removed);
      } else {
        // If beforeItem not found, add to end
        currentFields.push(removed);
      }
    }

    setBudgetFields(currentFields);

    // Save first 6 items as the hover order
    const hoverRecords = currentFields.slice(0, 6).filter(item => item).map((item) => item.budgetField);
    userPreferences.updateSetting(
      PERSISTENCE_STATE_KEYS.budgetHoverOrder,
      JSON.stringify(hoverRecords),
      true
    );
  };

  const handleBudgetLabelChange = (value: TComboSelectedValue | null) => {
    if (!userPreferences) return;

    const newValue = value as string;
    setBudgetLabel(newValue);
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.budgetLabel, newValue, true);
  };

  const handleActualDaysChange = (value: TComboSelectedValue | null) => {
    if (!userPreferences) return;

    const newValue = Array.isArray(value) ? value : [];
    setActualDaysResourceTypes(newValue as number[]);
    userPreferences.updateSetting(
      PERSISTENCE_STATE_KEYS.budgetActualDays,
      JSON.stringify(newValue),
      true
    );
  };

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<BudgetField>
        items={budgetFields}
        onReorder={handleReorderFields}
      >

        <Card className="py-3 px-4">
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">Budget Hover</h3>
            <p className="text-xs text-muted-foreground">
              Drag to reorder budget fields. The first 6 items (shown in orange) will be displayed in the budget hover tooltip.
            </p>
          </div>

          <SwivuList.Content<BudgetField> emptyMessage="No budget fields available." className="overflow-y-auto h-[20rem] py-4">
            {(field: BudgetField) => {
              const index = budgetFields.findIndex(f => f.id === field.id);
              return (
                <SwivuList.Item value={field.budgetField} className="bg-background">
                  {/* Drag Handle */}
                  <SwivuList.ItemHandle value={field.budgetField} />

                  {/* Field Name */}
                  <div className="flex-1 min-w-0">
                    <span
                      className={index !== -1 && index < 6 ? "text-[#da924c] font-medium" : ""}
                    >
                      {field.budgetFieldTitle}
                    </span>
                  </div>
                </SwivuList.Item>
              );
            }}
          </SwivuList.Content>
        </Card>

        {/* Budget Label */}
        <Card className="py-3 px-4">
          <div className="space-y-2">
            <Label htmlFor="budget-label">Budget Label</Label>
            <Combobox
              options={budgetLabelOptions}
              value={budgetLabel}
              onValueChange={handleBudgetLabelChange}
              placeholder="Select budget label..."
              searchPlaceholder="Search budget fields..."
              emptyMessage="No budget fields found"
              multiSelect={false}
              size="md"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Select which budget field to display as the label on task events in the timeline.
            </p>
          </div>
        </Card>

        {/* Actual Days */}
        <Card className="py-3 px-4">
          <div className="space-y-2">
            <Label htmlFor="actual-days">Actual Days</Label>
            <Combobox
              options={resourceTypeOptions}
              value={actualDaysResourceTypes}
              onValueChange={handleActualDaysChange}
              placeholder="Resource Types included when summing Actual Days"
              searchPlaceholder="Search resource types..."
              emptyMessage="No resource types found"
              multiSelect={true}
              size="md"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Select which resource types should be included when calculating actual days in the budget.
            </p>
          </div>
        </Card>
      </SwivuList.Root>
    </div>
  );
};

export default BudgetSettingsDetail;
